import React, { useState, useContext } from 'react';
import { AddressInput } from '../address-input';
import { Primary as PrimaryButton } from '../../../button';
import { Input } from '../../../input';
import { ModalContext } from '../../../modal';
import { InfoBox, InfoBoxTypes } from '../../../infobox';
import styled from 'styled-components';
import { typography } from '../../../../theme';
import { Checkbox } from '../../../checkbox/Checkbox';
import { useUser } from '../../../../hooks/useUser';
import { trackInsightsException } from '../../../../utils/telemetry/appinsights';
import {
  CreateUserAddressRequest,
  UserAddressDto,
} from '@kotipizzagroup/kotipizza-users-api-client';
import { UsersApiError } from '../../../../clients/users-api';
import { InlineError } from '../../../error/InlineError';
import { colors } from '../../../../theme/color';

type CreateAddressProps = {
  createAddress: (address: CreateUserAddressRequest) => Promise<UserAddressDto>;
  setDefaultAddress: (address: UserAddressDto) => Promise<void>;
  breakpoint?: number;
};

export const CreateAddress: React.FC<CreateAddressProps> = ({
  createAddress,
  setDefaultAddress,
  breakpoint,
}) => {
  const [address, setAddress] = useState<UserAddressDto>();
  const [isAddressValid, setAddressValid] = useState<boolean>(false);
  const { closeModal } = useContext(ModalContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [primaryAddress, setPrimaryAddress] = useState(false);
  const { user } = useUser();

  const handleAddressInputSelected = (inputAddress: UserAddressDto): void => {
    const apartmentNumber = address?.apartmentNumber;
    const newAddress = { ...inputAddress, apartmentNumber };
    setAddress(newAddress);
    setAddressValid(true);
  };

  const handlePrimaryAddress = (): void => {
    setPrimaryAddress(!primaryAddress);
  };

  const handleAddressSubmit = async (): Promise<void> => {
    try {
      if (!address || isLoading) {
        return;
      }
      setIsLoading(true);
      const newAddress = await createAddress(address);
      if ((primaryAddress || user?.addresses.length === 0) && setDefaultAddress) {
        await setDefaultAddress(newAddress);
      }
      setIsLoading(false);
      closeModal();
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
      trackInsightsException('FRONTEND_CREATE_ADDRESS_ERROR', error);
      if (error instanceof UsersApiError) {
        setError(error.message);
      }
    }
  };

  return (
    <>
      {isError && (
        <InfoBox type={InfoBoxTypes.ERROR}>
          <InlineError>{error}</InlineError>
        </InfoBox>
      )}

      <AddressInput
        type="primary"
        placeholder="Esim. Mariankatu 4"
        onAddressSelect={handleAddressInputSelected}
        onApartmentNumberChange={(e) => {
          setAddress({ ...address, apartmentNumber: e.target.value } as UserAddressDto);
        }}
        onInputValueChange={() => isAddressValid && setAddressValid(false)}
        aria-label="Lisätiedot kuljettajalle, esim. ovikoodi, kerros, yritys, kontaktiton toimitus"
        breakpoint={breakpoint}
      />

      <StyledInput
        width="full"
        label="Lisätiedot kuljettajalle"
        aria-describedby="additionalInfoLabel"
        rows={3}
        onChange={(e) => setAddress({ ...address, extraInfo: e.target.value } as UserAddressDto)}
      />
      <Label id="additionalInfoLabel">Esim. ovikoodi, kerros, yritys, kontaktiton toimitus</Label>

      {Number(user?.addresses.length) > 0 && (
        <CheckBoxContainer>
          <Checkbox value={primaryAddress} onClick={handlePrimaryAddress} />
          <TextContainer>
            <typography.ParagraphMedium>Suosikkiosoite</typography.ParagraphMedium>
            <Label>Näytetään ensimmäisenä osoitelistassa</Label>
          </TextContainer>
        </CheckBoxContainer>
      )}
      {/* loading icon missing */}
      <PrimaryButton
        disabled={!address || !isAddressValid || isLoading}
        onClick={handleAddressSubmit}
      >
        Tallenna osoite
      </PrimaryButton>
    </>
  );
};

const StyledInput = styled(Input)`
  margin: 20px auto 5px;
`;

export const Label = styled.label`
  ${typography.textSmallStyle}
  color: ${colors.gray60};
  margin-bottom: 24px;
  font-weight: normal;
  display: block;
`;

const CheckBoxContainer = styled.div`
  display: flex;
  flex-direction: row;
`;
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0px;
`;
